import React, { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import ShowChartIcon from '@mui/icons-material/ShowChart';

const AccountingPermissions = ({ 
  permissions = [], 
  selectedPermissions = {}, 
  onPermissionChange
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    accounting_overview: false,
    accounting_banking: false,
    accounting_receivables: false,
    accounting_payables: false,
    accounting_forecast: false
  });

  // Accounting pages mapping
  const accountingPages = ['overview', 'banking', 'receivables', 'payables', 'forecast'];
  const pageNameMapping = {
    'overview': 'Accounting Overview Page',
    'banking': 'Banking Page',
    'receivables': 'Receivables Page',
    'payables': 'Payables Page',
    'forecast': 'Forecast Page'
  };
  const pageIconMapping = {
    'overview': <InsightsIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'banking': <AccountBalanceIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'receivables': <ReceiptLongIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'payables': <CreditScoreIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />,
    'forecast': <ShowChartIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
  };

  const renderAccountingPagePermissions = (pageKey, pageName) => {
    const pagePermissions = permissions.filter(p => 
      p.name && p.name.includes(`accounting.${pageKey}`)
    );

    if (pagePermissions.length === 0) return null;

    const viewPerm = pagePermissions.find(p => p.name?.includes('.view'));
    const editPerm = pagePermissions.find(p => p.name?.includes('.edit'));
    const deletePerm = pagePermissions.find(p => p.name?.includes('.delete'));

    const isExpanded = expandedSections[`accounting_${pageKey}`] !== false;

    return (
      <Box 
        key={pageKey}
        id={`accounting-${pageKey}-section`}
        sx={{ 
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        {/* Page Header */}
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [`accounting_${pageKey}`]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {pageIconMapping[pageKey]}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {pageName}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        {/* Permission Switches */}
        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {/* View Permission */}
            {viewPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    View
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can view {pageName.toLowerCase()} page
                  </Typography>
                </Box>
                <Switch
                  checked={selectedPermissions[viewPerm.id]?.read || selectedPermissions[viewPerm.id]?.can_read || false}
                  onChange={(e) => onPermissionChange(viewPerm.id, 'can_read', e.target.checked)}
                  size="small"
                />
              </Box>
            )}

            {/* Edit Permission */}
            {editPerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Edit
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can edit {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={selectedPermissions[editPerm.id]?.write || selectedPermissions[editPerm.id]?.can_write || false}
                  onChange={(e) => onPermissionChange(editPerm.id, 'can_write', e.target.checked)}
                  size="small"
                />
              </Box>
            )}

            {/* Delete Permission */}
            {deletePerm && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Delete
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Can delete {pageName.toLowerCase()} data
                  </Typography>
                </Box>
                <Switch
                  checked={selectedPermissions[deletePerm.id]?.delete || selectedPermissions[deletePerm.id]?.can_delete || false}
                  onChange={(e) => onPermissionChange(deletePerm.id, 'can_delete', e.target.checked)}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {accountingPages.map(pageKey => 
        renderAccountingPagePermissions(pageKey, pageNameMapping[pageKey])
      )}
    </Box>
  );
};

export default AccountingPermissions;